<script>
    import { mqttDevices, mqttEvents } from "../../../service/mqtt";
    import { onMount, afterUpdate } from "svelte";

    export let deviceId = "";

    $: device = $mqttDevices[deviceId];
    $: deviceEvents = $mqttEvents.filter((e) => e.device_id === deviceId);

    let logsContainer;

    afterUpdate(() => {
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    });

    function formatTime(timestamp) {
        if (!timestamp) return "-";
        return new Date(timestamp).toLocaleString();
    }

    function formatUptime(seconds) {
        if (!seconds) return "-";
        const d = Math.floor(seconds / (3600 * 24));
        const h = Math.floor((seconds % (3600 * 24)) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${d}d ${h}h ${m}m`;
    }

    function miredToHex(mired) {
        if (!mired) return "#ffffff";
        // Clamp mired 153 (6500K) to 500 (2000K)
        const clamped = Math.max(153, Math.min(500, mired));
        const t = (clamped - 153) / (500 - 153); // 0 to 1

        // White (Cold) -> Orange (Warm)
        const r1 = 224,
            g1 = 240,
            b1 = 255;
        const r2 = 255,
            g2 = 176,
            b2 = 80;

        const r = Math.round(r1 + (r2 - r1) * t);
        const g = Math.round(g1 + (g2 - g1) * t);
        const b = Math.round(b1 + (b2 - b1) * t);

        return `rgb(${r},${g},${b})`;
    }

    function getIconColor(d) {
        if (d.color_temp) {
            return miredToHex(parseInt(d.color_temp));
        }
        if (d.color && d.color.length >= 6) {
            return `#${d.color.substring(0, 6)}`;
        }
        return "#000000";
    }

    // 색상 분석: 10자리 문자열 (RRGGBBWWCC)
    function parseColor(colorStr) {
        if (!colorStr || colorStr.length < 10) {
            return { rgb: "000000", ct: "0000", isRgbMode: false, isCTMode: false };
        }
        const rgb = colorStr.substring(0, 6);
        const ct = colorStr.substring(6, 10);
        const isRgbMode = rgb !== "000000";
        const isCTMode = ct !== "0000";
        return { rgb, ct, isRgbMode, isCTMode };
    }

    // RGB → Hue 위치 (0-100%)
    function rgbToHuePosition(rgbHex) {
        if (!rgbHex || rgbHex === "000000") return 0;
        const r = parseInt(rgbHex.substring(0, 2), 16) / 255;
        const g = parseInt(rgbHex.substring(2, 4), 16) / 255;
        const b = parseInt(rgbHex.substring(4, 6), 16) / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        if (max === min) return 0;
        let h = 0;
        const d = max - min;
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
        return h * 100;
    }

    // CT → 위치 (153=0%, 500=100%)
    function ctToPosition(ctValue) {
        if (!ctValue) return 0;
        const ct = parseInt(ctValue);
        return Math.max(0, Math.min(100, ((ct - 153) / (500 - 153)) * 100));
    }

    // Dimmer → 위치
    function dimmerToPosition(dimmer) {
        return parseInt(dimmer) || 0;
    }

    $: colorParsed = device ? parseColor(device.color) : { rgb: "000000", ct: "0000", isRgbMode: false, isCTMode: false };
    $: huePos = rgbToHuePosition(colorParsed.rgb);
    $: ctPos = ctToPosition(device?.color_temp);
    $: dimmerPos = dimmerToPosition(device?.dimmer);
</script>

{#if device}
    <div class="card h-100">
        <div
            class="card-header d-flex justify-content-between align-items-center"
        >
            <h5 class="mb-0">
                <i class="bx bx-detail me-2"></i> Device Detail
            </h5>
            <span
                class="badge {device.online === 'true'
                    ? 'bg-success'
                    : 'bg-danger'}"
            >
                {device.online === "true" ? "ONLINE" : "OFFLINE"}
            </span>
        </div>

        <div class="card-body p-0">
            <div class="row g-0 h-100">
                <!-- Left: Fixed Info Area -->
                <div
                    class="col-md-6 border-end p-4 overflow-auto"
                    style="max-height: 800px;"
                >
                    <!-- Key Status -->
                    <div class="row mb-4">
                        <div class="col-6">
                            <div
                                class="p-3 border rounded text-center mb-2 bg-light"
                            >
                                <small class="text-muted d-block uppercase"
                                    >Power</small
                                >
                                <h3
                                    class="mb-0 {device.power === 'ON'
                                        ? 'text-warning'
                                        : 'text-secondary'}"
                                >
                                    {device.power || "-"}
                                </h3>
                            </div>
                        </div>
                        <div class="col-6">
                            <div
                                class="p-3 border rounded text-center mb-2 bg-light"
                            >
                                <small class="text-muted d-block uppercase"
                                    >Dimmer</small
                                >
                                <h3 class="mb-0 text-primary">
                                    {device.dimmer !== undefined
                                        ? device.dimmer + "%"
                                        : "-"}
                                </h3>
                            </div>
                        </div>
                    </div>

                    <!-- Detailed Info -->
                    <h6
                        class="text-muted text-uppercase font-size-sm mb-3 border-bottom pb-2"
                    >
                        Information
                    </h6>
                    <table class="table table-sm table-borderless mb-4">
                        <tbody>
                            <tr
                                ><th class="text-muted w-25">Name</th><td
                                    >{device.name}</td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">ID</th><td
                                    ><code>{device.mqtt_id}</code></td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">IP</th><td
                                    >{device.ip_address}</td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">MAC</th><td
                                    >{device.mac_address || "-"}</td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">WiFi</th><td
                                    >{device.wifi_ssid} ({device.wifi_rssi}%)</td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">Uptime</th><td
                                    >{formatUptime(device.uptime)}</td
                                ></tr
                            >
                            <tr
                                ><th class="text-muted">Last Seen</th><td
                                    >{formatTime(device.last_seen)}</td
                                ></tr
                            >
                        </tbody>
                    </table>

                    <!-- Lighting Info -->
                    <h6
                        class="text-muted text-uppercase font-size-sm mb-3 border-bottom pb-2"
                    >
                        Lighting
                    </h6>

                    <!-- Color Sliders (Tasmota Style) -->
                    <div style="background: #f8f9fa; border-radius: 8px; padding: 16px;">
                        <!-- Brightness Slider -->
                        <div style="margin-bottom: 16px;">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                                <span>Brightness</span>
                                <span style="font-weight: 600; background: #e9ecef; padding: 2px 8px; border-radius: 4px;">{device.dimmer || 0}%</span>
                            </div>
                            <div style="position: relative; height: 24px; border-radius: 12px; background: linear-gradient(to right, #1a1a1a, #ffffff); box-shadow: inset 0 2px 4px rgba(0,0,0,0.15);">
                                <div style="position: absolute; top: 2px; left: calc({dimmerPos}% - 10px); width: 20px; height: 20px; border-radius: 50%; background: #ffffff; border: 3px solid #ccc; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>
                            </div>
                        </div>

                        <!-- RGB Hue Slider -->
                        <div style="margin-bottom: 16px; {colorParsed.isRgbMode ? '' : 'opacity: 0.35;'}">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                                <span>
                                    RGB Color
                                    {#if colorParsed.isRgbMode}
                                        <span style="background: #28a745; color: #fff; font-size: 0.65rem; padding: 1px 6px; border-radius: 3px; margin-left: 6px;">ON</span>
                                    {/if}
                                </span>
                                <span style="font-weight: 600; padding: 2px 8px; border-radius: 4px; background-color: #{colorParsed.rgb}; color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">
                                    #{colorParsed.rgb}
                                </span>
                            </div>
                            <div style="position: relative; height: 24px; border-radius: 12px; background: linear-gradient(to right, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%)); box-shadow: inset 0 2px 4px rgba(0,0,0,0.15); {colorParsed.isRgbMode ? '' : 'filter: grayscale(0.8);'}">
                                <div style="position: absolute; top: 2px; left: calc({huePos}% - 10px); width: 20px; height: 20px; border-radius: 50%; background: #{colorParsed.rgb}; border: 3px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>
                            </div>
                        </div>

                        <!-- Color Temperature Slider -->
                        <div style="{colorParsed.isCTMode ? '' : 'opacity: 0.35;'}">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                                <span>
                                    Color Temp
                                    {#if colorParsed.isCTMode}
                                        <span style="background: #28a745; color: #fff; font-size: 0.65rem; padding: 1px 6px; border-radius: 3px; margin-left: 6px;">ON</span>
                                    {/if}
                                </span>
                                <span style="font-weight: 600; background: #e9ecef; padding: 2px 8px; border-radius: 4px;">{device.color_temp || 153} mired</span>
                            </div>
                            <div style="position: relative; height: 24px; border-radius: 12px; background: linear-gradient(to right, #cce6ff, #ffc56e); box-shadow: inset 0 2px 4px rgba(0,0,0,0.15); {colorParsed.isCTMode ? '' : 'filter: grayscale(0.8);'}">
                                <div style="position: absolute; top: 2px; left: calc({ctPos}% - 10px); width: 20px; height: 20px; border-radius: 50%; background: {miredToHex(parseInt(device.color_temp || 153))}; border: 3px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Raw Values -->
                    <div class="small text-muted border-top pt-2">
                        <div class="d-flex justify-content-between">
                            <span>Raw Color:</span>
                            <code>{device.color || "-"}</code>
                        </div>
                    </div>
                </div>

                <!-- Right: Realtime Logs (Console Style) -->
                <div
                    class="col-md-6 bg-dark text-light p-0 d-flex flex-column"
                    style="min-height: 400px; max-height: 800px;"
                >
                    <div
                        class="p-2 border-bottom border-secondary bg-black bg-opacity-25 d-flex justify-content-between align-items-center"
                    >
                        <small
                            class="text-uppercase font-monospace text-white-50"
                        >
                            <i class="bx bx-terminal me-1"></i>Live Logs
                        </small>
                        <span
                            class="badge bg-secondary font-monospace"
                            style="font-size: 0.7rem;">tail -f</span
                        >
                    </div>

                    <div
                        bind:this={logsContainer}
                        class="flex-grow-1 overflow-auto p-2 font-monospace"
                        style="font-size: 0.8rem; background-color: #1a1a1a;"
                    >
                        {#each deviceEvents as event (event.timestamp + event.topic)}
                            <div class="mb-1 d-flex">
                                <span class="text-secondary me-2 flex-shrink-0"
                                    >[{new Date(
                                        event.timestamp,
                                    ).toLocaleTimeString()}]</span
                                >
                                <div class="text-break">
                                    <span class="text-info me-1"
                                        >{event.topic}</span
                                    >
                                    <span class="text-light"
                                        >{event.payload}</span
                                    >
                                </div>
                            </div>
                        {/each}
                        {#if deviceEvents.length === 0}
                            <div
                                class="text-secondary opacity-50 fst-italic p-2"
                            >
                                Waiting for events...
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
{:else}
    <div
        class="card h-100 d-flex align-items-center justify-content-center text-muted bg-light border-dashed"
    >
        <div class="text-center">
            <i class="bx bx-devices fs-1 mb-3 text-secondary"></i>
            <p>Select a device from the list to view details</p>
        </div>
    </div>
{/if}

<style>
    /* No scoped styles - using inline styles for sliders */
</style>
